import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthAPI } from "../api";
import { useAuth } from "../context/AuthContext";
import { clearStoredAuth, persistAuthTokens } from "../utils/authStorage";

export default function Login() {
  const navigate = useNavigate();
  const { user, completeLogin, isAuthenticated, isValidating } = useAuth();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const successMessage = location.state?.success;

  useEffect(() => {
    if (!isValidating && isAuthenticated && user) {
      navigate(user.is_mentor ? "/dashboard" : "/mentors", { replace: true });
    }
  }, [isAuthenticated, isValidating, navigate, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await AuthAPI.post("auth/login/", {
        ...form,
        username: form.username.trim().toLowerCase(),
      });
      persistAuthTokens(res.data);

      const userRes = await AuthAPI.get("auth/me/");
      completeLogin(userRes.data);

      navigate(userRes.data.is_mentor ? "/dashboard" : "/mentors");
    } catch (err) {
      // Clear any tokens that may have been partially stored before the failure
      clearStoredAuth();
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Unable to log in. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8">

        {/* Brand mark */}
        <div className="flex justify-center mb-6">
          <img src="/logo.svg" alt="Hindsight" className="h-20 w-auto" />
        </div>

        {successMessage && (
          <div className="mb-5 px-4 py-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Header */}
        <h2 className="text-2xl font-semibold text-center text-gray-900 mb-1">
          Welcome back
        </h2>
        <p className="text-sm text-center text-gray-500 mb-7">
          Log in to your Hindsight account
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email address
            </label>
            <input
              id="login-email"
              type="email"
              autoFocus
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm shadow-sm focus:ring-2 focus:ring-[#282E6A] focus:border-[#282E6A] focus:outline-none transition"
              placeholder="Enter your email address"
              aria-describedby={error ? "login-error" : undefined}
              required
            />
          </div>

          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm shadow-sm focus:ring-2 focus:ring-[#282E6A] focus:border-[#282E6A] focus:outline-none transition"
              placeholder="Enter your password"
              aria-describedby={error ? "login-error" : undefined}
              required
            />
          </div>

          {error && (
            <div id="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 rounded-lg text-sm font-medium transition mt-2 ${
              loading
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#282E6A] hover:bg-[#1e2458] text-white"
            }`}
          >
            {loading ? "Logging in…" : "Log in"}
          </button>
        </form>

        {/* Footer */}
        <p className="mt-6 text-sm text-center text-gray-500">
          New here?{" "}
          <Link to="/register" className="font-medium text-[#282E6A] hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
